// The hover card. Everything hovered explains itself, and this is the surface
// it explains itself on (PLAN-UI §28.2; designer ruling, U6 review).
//
// **Native `title` is out.** A browser tooltip is a system chrome rectangle in
// the system font on the system's own clock: it cannot be styled, it appears
// about a second late in a place the interface did not choose, it truncates
// nothing and wraps badly, and it is invisible to the mock. The reference for
// what a hover should look like is the stock pill's expansion in
// `ReadoutPills.tsx` — a panel in the app's own language, appearing at once and
// laid out on purpose. This is that, as one primitive, so the twenty places
// that were reaching for `title` say it the same way.
//
// **Popper, not MUI's `Tooltip`.** Same argument `Popover.tsx` makes against
// `Popover`: what is wanted is the positioning engine — flip, shift, portal —
// and not the surface, the timing or the transition, all of which are the
// design. `Tooltip` also insists on being described by `aria-label`/`title`
// semantics that fight a trigger already carrying its own name.
//
// **It must work on `aria-disabled` triggers**, which is the whole reason
// `Tile` refuses the native `disabled` attribute: an off control's one job is
// to say *why*, and a control with `pointer-events: none` can never be
// hovered. So this attaches pointer handlers and nothing else — no `disabled`
// gate on the DOM, only on whether the card opens.
//
// **Three rules from the U6 review, all of them one rule: a card explains a
// control and must never stand between the player and it.**
//
// 1. **A card never holds itself open.** The surface takes no pointer, so
//    leaving the trigger closes it after the grace whether or not the pointer
//    is now over the card. Written in `hoverState.ts`.
// 2. **A press on the trigger closes the card and latches it shut** until the
//    pointer leaves and re-enters, or focus leaves and returns. Pressing is
//    using the control; the sentence has been read.
// 3. **A card opens away from the trigger's siblings**, which is why
//    `placement` has no default: in a column (the hotbar, the tool rail) it is
//    a side, in a row (the build flyout, the turn row, a swatch row, the
//    inspector's controls) it is `top` or `bottom`. A guessed default put
//    cards on the neighbouring button every time. **And a trigger that opens a
//    panel passes `disabled` while that panel is up** — the card would land on
//    the thing the click just opened, one rung above it.
// 4. **A card inside a panel opens beside the panel** (second review): pass
//    `beside`. `right` of a trigger at a 240px card's left edge is still
//    inside that card, so the direction is not the whole answer — the anchor
//    has to be the panel's edge. `besideRect` in `hoverAnchor.ts`.
//
// Timing lives in `hoverState.ts` and is tested there. This file is the DOM.

import {
  Popper,
  type PopperPlacementType,
  type PopperProps,
  keyframes,
  styled
} from "@mui/material";
import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useReducer,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
  type Ref
} from "react";

import { Panel } from "./Panel";
import { REDUCED_MOTION, STILL } from "./motion";
import { besideRect } from "./hoverAnchor";
import { RESTING, hoverStep } from "./hoverState";

/** The pause before a hover means anything. */
const DELAY_MS = 120;

/**
 * How long the card survives the pointer leaving.
 *
 * **Debounce, not a bridge** (U6 review): it is here so a pointer that jitters
 * across a tile's own border does not strobe the card, and for nothing else —
 * the card cannot be hovered, so there is no gap to cross.
 */
const GRACE_MS = 90;

/** Clear of the trigger. Nothing has to reach across it. */
const OFFSET: PopperProps["modifiers"] = [{ name: "offset", options: { offset: [0, 8] } }];

const rise = keyframes({
  from: { opacity: 0, transform: "translateY(4px)" },
  to: { opacity: 1, transform: "translateY(0)" }
});

// One rung above everything, `dev` included — a card explaining a control is
// useless underneath the surface that control is on. See `tokens.ts`.
const Positioned = styled(Popper)(({ theme }) => ({ zIndex: theme.kit.z.tooltip }));

const Surface = styled(Panel)(({ theme }) => ({
  // **The card takes no pointer** (rule 1). It is a sentence about a control,
  // never a surface to visit — so it cannot hold itself open, cannot swallow a
  // click meant for what is under it, and cannot be the reason a player has to
  // steer round their own tooltip.
  pointerEvents: "none",
  // So `maxWidth` caps the card rather than its text: `Panel` carries a 2px
  // border and this adds padding, which content-box would put outside the cap.
  boxSizing: "border-box",
  padding: `${theme.kit.space.sm}px ${theme.kit.space.md}px`,
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs,
  // **Entry only**, `Popover`'s ruling for its reason: a surface that animates
  // out has to stay mounted while it does, and the pointer has already left.
  animation: `${rise} ${theme.kit.motion.duration.fast}ms ${theme.kit.motion.easeBounce} both`,
  [REDUCED_MOTION]: STILL
}));

/** The heading line — what the thing is called. */
const Heading = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.heading,
  fontSize: 15,
  lineHeight: 1.2,
  color: theme.kit.color.ink
}));

/** A string card: one line of explanation, in the secondary ink. */
const Body = styled("div")(({ theme }) => ({
  fontFamily: theme.kit.font.body,
  fontSize: 12,
  lineHeight: 1.45,
  color: theme.kit.color.inkDim
}));

export interface HoverCardProps {
  /** The trigger. Rendered as-is; the card hangs off it. Must accept a ref. */
  children: ReactElement;
  /** The card's content. A string is drawn as one body line; a node as given. */
  card: ReactNode;
  /** Optional heading line in the heading font. */
  title?: ReactNode;
  /**
   * **Required, because the right answer is a fact about the layout** (rule 3).
   * Open across the axis the trigger's siblings run on: a side in a column, a
   * `top` or `bottom` in a row, whichever is away from the board and the rest
   * of the chrome. There is no default that is right twice.
   */
  placement: PopperPlacementType;
  /**
   * The panel the trigger lives in, when it lives in one (rule 4).
   *
   * With it the card hangs off that panel's right edge at the trigger's own
   * height rather than off the trigger itself, so `right` means *outside the
   * panel* and not merely "a little to the right of a control that is already
   * at the panel's left edge". The trigger stays the event source and the
   * described element; only the geometry moves.
   */
  beside?: HTMLElement | null;
  /** Milliseconds before it opens on hover. Focus opens at once. */
  delay?: number;
  /** Width cap. */
  maxWidth?: number;
  /**
   * Never opens. For a trigger with nothing to say, not for an off control —
   * and **for a trigger whose panel is open** (rule 3), where the card would
   * cover what the click just produced.
   */
  disabled?: boolean;
  "data-testid"?: string;
}

/** What this reads off the trigger and puts back, chained rather than replaced. */
interface Triggered {
  ref?: Ref<HTMLElement>;
  "aria-describedby"?: string;
  onPointerEnter?(event: PointerEvent<HTMLElement>): void;
  onPointerLeave?(event: PointerEvent<HTMLElement>): void;
  onPointerDown?(event: PointerEvent<HTMLElement>): void;
  onFocus?(event: FocusEvent<HTMLElement>): void;
  onBlur?(event: FocusEvent<HTMLElement>): void;
  onClick?(event: MouseEvent<HTMLElement>): void;
}

export function HoverCard({
  children,
  card,
  title,
  placement,
  beside,
  delay = DELAY_MS,
  maxWidth = 260,
  disabled,
  "data-testid": testId
}: HoverCardProps) {
  const [state, dispatch] = useReducer(hoverStep, RESTING);
  // A state ref rather than `useRef`: Popper needs the element on the render
  // that opens, and `.current` on that render is still null.
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Whether the gesture that reached the trigger was a finger. A coarse
  // pointer has no hover, so it toggles on the tap instead.
  const coarse = useRef(false);
  const id = useId();

  const own = children.props as Triggered;
  const theirRef = own.ref;

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      setAnchor(node);
      if (typeof theirRef === "function") theirRef(node);
      else if (theirRef) (theirRef as { current: HTMLElement | null }).current = node;
    },
    [theirRef]
  );

  // The two timers, both driven off `state.timer` so the machine owns which is
  // running and this owns only how long it is.
  const pending = state.timer;
  useEffect(() => {
    if (pending === null) return;
    const timer = window.setTimeout(
      () => dispatch({ kind: pending === "open" ? "delay" : "grace" }),
      pending === "open" ? delay : GRACE_MS
    );
    return () => window.clearTimeout(timer);
  }, [pending, delay]);

  // A trigger that goes off while its card is up takes the card with it.
  useEffect(() => {
    if (disabled) dispatch({ kind: "off" });
  }, [disabled]);

  // Escape, and a click away. Both only while open, so a screen of forty
  // explained controls puts no handlers on the document at rest.
  //
  // **Capture and `stopPropagation`**, `Popover`'s reason: `GameShell` listens
  // for Escape on `window` and registers first, so a bubble listener here
  // would close this *and* open the pause menu behind it.
  useEffect(() => {
    if (!state.open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      dispatch({ kind: "escape" });
    };
    const onDown = (event: globalThis.PointerEvent) => {
      // The trigger's own handler answers a press on itself (rule 2), and it
      // is the one that knows whether the gesture was a finger. The card takes
      // no pointer at all, so it can never be the target here.
      if (anchor?.contains(event.target as Node)) return;
      dispatch({ kind: "escape" });
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [state.open, anchor]);

  const open = state.open && anchor !== null && !disabled;

  // **The trigger is the event source; this is only where the card starts**
  // (rule 4). A virtual element rather than a second real one, and measured on
  // every Popper update rather than stored, because the panel scrolls and the
  // trigger moves inside it. Memoised on the pair so the identity moves when
  // either does — Popper re-reads an anchor it is handed afresh.
  const anchorEl = useMemo<PopperProps["anchorEl"]>(() => {
    if (!anchor || !beside) return anchor;
    return {
      getBoundingClientRect: () =>
        besideRect(anchor.getBoundingClientRect(), beside.getBoundingClientRect()) as DOMRect
    };
  }, [anchor, beside]);

  const trigger = cloneElement(children as ReactElement<Record<string, unknown>>, {
    ref: setRef,
    // Announced as the trigger's description, so a screen reader gets the
    // explanation without the trigger losing its own name.
    "aria-describedby": open ? join(own["aria-describedby"], id) : own["aria-describedby"],
    onPointerDown(event: PointerEvent<HTMLElement>) {
      coarse.current = event.pointerType === "touch";
      own.onPointerDown?.(event);
      // **Pressing puts the card away and latches it** (rule 2). Not on a
      // finger: a coarse pointer has no hover, so `onClick` below toggles
      // instead and a latch here would make the second tap reopen it.
      if (!coarse.current) dispatch({ kind: "press" });
    },
    onPointerEnter(event: PointerEvent<HTMLElement>) {
      own.onPointerEnter?.(event);
      if (disabled || event.pointerType === "touch") return;
      dispatch({ kind: "enter" });
    },
    onPointerLeave(event: PointerEvent<HTMLElement>) {
      own.onPointerLeave?.(event);
      if (event.pointerType === "touch") return;
      dispatch({ kind: "leave" });
    },
    onFocus(event: FocusEvent<HTMLElement>) {
      own.onFocus?.(event);
      // Keyboard focus only. A click focuses a button too, and a card that
      // appeared on every click would be in the way of the next one.
      if (disabled || !keyboardFocus(event.currentTarget)) return;
      dispatch({ kind: "focus" });
    },
    onBlur(event: FocusEvent<HTMLElement>) {
      own.onBlur?.(event);
      dispatch({ kind: "blur" });
    },
    onClick(event: MouseEvent<HTMLElement>) {
      own.onClick?.(event);
      if (disabled || !coarse.current) return;
      dispatch({ kind: "tap" });
    }
  });

  return (
    <>
      {trigger}
      <Positioned open={open} anchorEl={anchorEl} placement={placement} modifiers={OFFSET}>
        {/* **No `role` here.** MUI's Popper root already carries
            `role="tooltip"`, and a tooltip nested inside a tooltip is two
            announcements of one card. The `id` stays on this half, because it
            is the half with the words in it. */}
        <Surface
          id={id}
          data-testid={testId}
          // Data, not styling: a cap chosen per call site would otherwise mint
          // an emotion class per distinct number.
          style={{ maxWidth }}
        >
          {title !== undefined && title !== null && <Heading>{title}</Heading>}
          {typeof card === "string" ? <Body>{card}</Body> : card}
        </Surface>
      </Positioned>
    </>
  );
}

/** Two ids, or whichever one there is. */
function join(theirs: string | undefined, mine: string): string {
  return theirs ? `${theirs} ${mine}` : mine;
}

/**
 * Did the keyboard put focus here?
 *
 * `:focus-visible` is the browser's own answer to that question and is the
 * only honest one — "was the last input a key" is a heuristic this would
 * otherwise have to keep. Guarded because a selector a browser does not know
 * throws rather than answering false.
 */
function keyboardFocus(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    return false;
  }
}
